/**
 * usePicksStream Hook
 *
 * Frontend SSE consumer for streaming pick generation.
 * Uses fetch() + ReadableStream (NOT EventSource — needs POST body).
 *
 * Flow:
 * 1. POST to /api/picks/stream with generation params
 * 2. Read SSE chunks from response.body
 * 3. Dispatch events to generation store (phase updates, parlay arrivals, completion)
 * 4. Falls back to regular POST /api/picks if streaming fails
 */

import { useAuth } from '@contexts/AuthContext';
import type {
  CompleteEvent,
  ErrorEvent,
  ParlayEvent,
  PhaseEvent,
  PickStreamEvent,
  QueuedEvent,
} from '@parlay-picker/shared/pickStreamEvents';
import { type ParlayScoreData, useGenerationStore } from '@stores/generationStore';
import { useCallback, useRef, useState } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface GeneratePicksStreamBody {
  gameIds?: string[];
  legHistory?: unknown[];
  forceRefresh?: boolean;
  markets?: string[];
  bannedPlayers?: string[];
  analysisMode?: string;
  analysisConfig?: Record<string, unknown>;
  sportsbook: string;
  provider?: string;
  league: string;
}

export interface UsePicksStreamResult {
  startStream: (body: GeneratePicksStreamBody, options?: { signal?: AbortSignal }) => Promise<void>;
  isStreaming: boolean;
  cancel: () => void;
  /** Set this ref to receive quota data when the SSE complete event includes it */
  onQuotaRef: React.MutableRefObject<((quota: CompleteEvent['quota']) => void) | null>;
}

// =============================================================================
// Hook
// =============================================================================

export function usePicksStream(): UsePicksStreamResult {
  const { authFetch } = useAuth();
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  /** Caller-provided callback fired when the complete event includes quota data */
  const onQuotaRef = useRef<((quota: CompleteEvent['quota']) => void) | null>(null);

  const { addStreamingParlay, setStreamingPhase, setQueuePosition, finalizeStreaming } =
    useGenerationStore();

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  // Dispatch non-error SSE events to generation store
  // biome-ignore lint/correctness/useExhaustiveDependencies: zustand actions are stable and don't need to be in deps
  const handleEvent = useCallback((event: PickStreamEvent) => {
    switch (event.type) {
      case 'queued': {
        const queuedEvent = event as QueuedEvent;
        setQueuePosition(queuedEvent.position, queuedEvent.estimatedWaitMs);
        break;
      }
      case 'phase':
        setStreamingPhase((event as PhaseEvent).phase, (event as PhaseEvent).message);
        break;
      case 'parlay': {
        const parlayEvent = event as ParlayEvent;
        const score = parlayEvent.score
          ? ({
              strategyScore: parlayEvent.score.strategyScore,
              predictionScore: parlayEvent.score.predictionScore,
            } as ParlayScoreData)
          : null;
        addStreamingParlay(parlayEvent.parlay, score);
        break;
      }
      case 'complete': {
        const completeEvent = event as CompleteEvent;
        // Build processed parlays + scores from complete event
        const processedParlays = completeEvent.parlays.map((p) => p.parlay);
        const processedScores: ParlayScoreData[] = completeEvent.parlays.map((p) => ({
          strategyScore: (p.score?.strategyScore ?? null) as ParlayScoreData['strategyScore'],
          predictionScore: (p.score?.predictionScore ?? null) as ParlayScoreData['predictionScore'],
        }));

        // Pass processed data directly to finalizeStreaming to avoid an intermediate
        // setState that causes a visible render flash (8 streaming → 4 processed → final state).
        // With overrides, finalizeStreaming does a single atomic set() call.
        finalizeStreaming(
          completeEvent.generationId ?? null,
          completeEvent.cached,
          completeEvent.statsWarning,
          completeEvent.lineMismatchWarning,
          processedParlays,
          processedScores,
        );
        if (completeEvent.quota) {
          onQuotaRef.current?.(completeEvent.quota);
        }
        break;
      }
    }
  }, []);

  const startStream = useCallback(
    async (body: GeneratePicksStreamBody, options?: { signal?: AbortSignal }) => {
      // Cancel any existing stream
      abortRef.current?.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      // Forward external signal (e.g., from generation store's cancel button)
      if (options?.signal) {
        if (options.signal.aborted) {
          controller.abort();
        } else {
          options.signal.addEventListener('abort', () => controller.abort(), { once: true });
        }
      }

      setIsStreaming(true);

      try {
        const response = await authFetch(`${API_BASE_URL}${API_ENDPOINTS.PICKS_STREAM}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        // Non-2xx: fall through to error handling
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const { error: errorMsg, errorType } = errorData as {
            error?: string;
            errorType?: string;
          };
          const err = new Error(errorMsg || `Stream request failed: ${response.status}`);
          // Attach metadata so callers can distinguish error types (e.g., 429 lock rejection)
          (err as Error & { status?: number; errorType?: string }).status = response.status;
          (err as Error & { status?: number; errorType?: string }).errorType = errorType;
          throw err;
        }

        if (!response.body) {
          throw new Error('No response body from stream endpoint');
        }

        // Parse SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamError: string | null = null;

        try {
          while (true) {
            if (controller.signal.aborted) break;

            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE events (separated by \n\n)
            const parts = buffer.split('\n\n');
            buffer = parts.pop() || '';

            for (const part of parts) {
              const lines = part.split('\n');
              for (const line of lines) {
                if (!line.startsWith('data: ')) continue;

                const jsonStr = line.slice(6);
                let event: PickStreamEvent;
                try {
                  event = JSON.parse(jsonStr);
                } catch {
                  continue; // Skip malformed JSON
                }

                if (event.type === 'error') {
                  streamError = (event as ErrorEvent).message;
                } else {
                  handleEvent(event);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }

        // If user cancelled (abort), throw AbortError so the caller knows
        // this was a cancellation, not a zero-picks completion.
        // reader.read() may return { done: true } instead of throwing on abort.
        if (controller.signal.aborted) {
          throw new DOMException('The operation was aborted.', 'AbortError');
        }

        // If the server sent an error event, throw after stream closes
        if (streamError) {
          throw new Error(streamError);
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          // Re-throw so the caller (PicksView) knows generation was cancelled
          // and doesn't treat it as a zero-picks completion
          throw err;
        }
        // Re-throw for the caller to handle (e.g., fallback to non-streaming)
        throw err;
      } finally {
        abortRef.current = null;
        setIsStreaming(false);
      }
    },
    [authFetch, handleEvent],
  );

  return { startStream, isStreaming, cancel, onQuotaRef };
}
